import { DateFormat, formatDate, now } from "common";
import { Dayjs } from "dayjs";
import { SelectionState } from "types";
import create from "zustand";

export enum DateFilterRange {
    CREATED_AT = "createdAt[range]",
    REALIZED_AT = "items.realizedAt[range]",
    ARRIVAL_DATE = "arrivalDate[range]",
    ITEM_ARRIVAL_DATE = "items.arrivalDate[range]",
}

export type CreatedAtRangeProps = Record<DateFilterRange.CREATED_AT, string>;
export type RealizedAtRangeProps = Record<DateFilterRange.REALIZED_AT, string>;
export type ArrivalDateRangeProps = Record<DateFilterRange.ARRIVAL_DATE, string>;
export type DateParamsProps = CreatedAtRangeProps | RealizedAtRangeProps | ArrivalDateRangeProps;

export interface AnalyticsFilter {
    products: SelectionState;
    mode: AnalyticsMode;
    view: AnalyticsView;
}

export enum AnalyticsMode {
    RECOGNIZED = "recognized",
    CASH_FLOW = "cash_flow",
    ARRIVAL_DATE = "arrival_date",
}

export enum AnalyticsView {
    CHART = "chart",
    TABLE = "table",
}

const defaultFilters: AnalyticsFilter = {
    products: { all: true, selected: [] },
    mode: AnalyticsMode.CASH_FLOW,
    view: AnalyticsView.CHART,
};

const defaultRange = () => {
    return { from: now().subtract(1, "month").startOf("day"), to: now().endOf("day") };
};

interface AnalyticsStoreState {
    to?: Dayjs;
    from?: Dayjs;
    getFrom: () => Dayjs;
    getTo: () => Dayjs;
    filters: AnalyticsFilter;
    setFilters: (filters: Partial<AnalyticsFilter>) => void;
    resetFilters: (filters?: Partial<AnalyticsFilter>) => void;
    setFrom: (date: Dayjs) => void;
    setTo: (date: Dayjs) => void;
    setRange: (from: Dayjs, to: Dayjs) => void;
    setMode: (mode: AnalyticsMode) => void;
    resetStore: () => void;
    getDateParams: () => Record<string, string>; // Not sure why `DateParamsProps` does not work
}

export const useAnalyticsFilterStore = create<AnalyticsStoreState>((set, get) => ({
    from: undefined,
    to: undefined,
    filters: defaultFilters,

    getDateParams() {
        const { mode } = get().filters;
        const name = mode === AnalyticsMode.RECOGNIZED ? DateFilterRange.REALIZED_AT : DateFilterRange.CREATED_AT;
        const from = get().getFrom();
        const to = get().getTo();
        const params = [formatDate(from, DateFormat.DATE_TIME_ISO), formatDate(to, DateFormat.DATE_TIME_ISO)];
        return { [name]: params.join(",") };
    },

    setFilters(filters: Partial<AnalyticsFilter>) {
        set((state) => ({ filters: { ...state.filters, ...filters } }));
    },

    resetFilters(filters?: Partial<AnalyticsFilter>) {
        set({ filters: { ...defaultFilters, ...filters } });
    },

    resetStore: () => {
        set(() => ({ from: defaultRange().from, to: defaultRange().to, filters: defaultFilters }));
    },

    getFrom() {
        return get().from ?? defaultRange().from;
    },

    getTo() {
        return get().to ?? defaultRange().to;
    },

    setFrom(from: Dayjs) {
        set(() => ({ from }));
    },

    setTo(to: Dayjs) {
        set(() => ({ to }));
    },

    setRange: (from: Dayjs, to: Dayjs) => {
        set(() => ({ from, to }));
    },

    setMode: (mode: AnalyticsMode) => {
        set((state) => ({ filters: { ...state.filters, mode } }));
    },
}));
